import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Cake, PartyPopper, Gift, Star } from "lucide-react";
import { format, parseISO } from "date-fns";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Confetti animation component
const Confetti = () => {
  const colors = ['#FF6B8A', '#FFB347', '#87CEEB', '#98FB98', '#DDA0DD', '#F0E68C', '#FF69B4', '#00CED1'];
  const confettiPieces = Array.from({ length: 50 }, (_, i) => ({
    id: i,
    left: Math.random() * 100,
    delay: Math.random() * 5,
    duration: 3 + Math.random() * 4,
    color: colors[Math.floor(Math.random() * colors.length)],
    size: 8 + Math.random() * 12,
    rotation: Math.random() * 360,
  }));

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-0">
      {confettiPieces.map((piece) => (
        <div
          key={piece.id}
          className="absolute animate-confetti-fall"
          style={{
            left: `${piece.left}%`,
            animationDelay: `${piece.delay}s`,
            animationDuration: `${piece.duration}s`,
          }}
        >
          <div
            style={{
              width: piece.size,
              height: piece.size,
              backgroundColor: piece.color,
              transform: `rotate(${piece.rotation}deg)`,
              borderRadius: Math.random() > 0.5 ? '50%' : '2px',
            }}
          />
        </div>
      ))}
    </div>
  );
};

// Balloon component
const Balloons = () => {
  const balloonColors = ['#FF6B8A', '#FFB347', '#87CEEB', '#98FB98', '#DDA0DD'];
  
  return (
    <div className="fixed inset-0 pointer-events-none z-0">
      {/* Left balloons */}
      <div className="absolute left-4 top-20 flex flex-col gap-2">
        {balloonColors.slice(0, 3).map((color, i) => (
          <div
            key={`left-${i}`}
            className="animate-balloon-float"
            style={{ animationDelay: `${i * 0.5}s` }}
          >
            <div
              className="w-16 h-20 rounded-full shadow-lg"
              style={{ backgroundColor: color }}
            />
            <div className="w-0.5 h-12 bg-gray-400 mx-auto" />
          </div>
        ))}
      </div>
      
      {/* Right balloons */}
      <div className="absolute right-4 top-20 flex flex-col gap-2">
        {balloonColors.slice(2, 5).map((color, i) => (
          <div
            key={`right-${i}`}
            className="animate-balloon-float"
            style={{ animationDelay: `${i * 0.7}s` }}
          >
            <div
              className="w-16 h-20 rounded-full shadow-lg"
              style={{ backgroundColor: color }}
            />
            <div className="w-0.5 h-12 bg-gray-400 mx-auto" />
          </div>
        ))}
      </div>
    </div>
  );
};

export default function DigitalSign() {
  const [weeklyData, setWeeklyData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());

  const fetchWeeklyBirthdays = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/birthdays/weekly`);
      setWeeklyData(response.data);
    } catch (error) {
      console.error("Error fetching birthdays:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchWeeklyBirthdays();
    
    // Auto-refresh every 5 minutes
    const refreshInterval = setInterval(fetchWeeklyBirthdays, 5 * 60 * 1000);
    
    // Update time every minute
    const timeInterval = setInterval(() => setCurrentTime(new Date()), 60 * 1000);
    
    return () => {
      clearInterval(refreshInterval);
      clearInterval(timeInterval);
    };
  }, [fetchWeeklyBirthdays]);

  const getTodaysBirthdays = () => {
    if (!weeklyData?.days) return [];
    const todayData = weeklyData.days.find(day => day.is_today);
    return todayData?.birthdays || [];
  };

  const formatBirthday = (dateStr) => {
    try {
      return format(parseISO(dateStr), "MMMM d");
    } catch {
      return dateStr;
    }
  };

  const todaysBirthdays = getTodaysBirthdays();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-400 via-purple-400 to-blue-400 flex items-center justify-center">
        <div className="text-white text-4xl font-bold animate-pulse">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-300 via-purple-200 to-blue-300 overflow-hidden relative">
      {/* Animated background elements */}
      <Confetti />
      <Balloons />
      
      {/* Main content */}
      <div className="relative z-10 p-8">
        {/* Header with Logo */}
        <header className="text-center mb-8">
          <div className="flex items-center justify-center gap-4 mb-4">
            <img 
              src="/rr-logo.png" 
              alt="Rolling River Day Camp" 
              className="w-24 h-24 rounded-2xl shadow-xl bg-white p-2"
            />
            <div>
              <h1 className="font-['Fredoka'] text-5xl md:text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-pink-600 via-purple-600 to-blue-600 drop-shadow-lg">
                RRDC Confetti OS
              </h1>
              <p className="font-['Outfit'] text-2xl text-white drop-shadow-md mt-2">
                🎉 Birthday Celebrations 🎉
              </p>
            </div>
          </div>
          <p className="font-['Outfit'] text-xl text-white/80">
            {format(currentTime, "EEEE, MMMM d, yyyy • h:mm a")}
          </p>
        </header>

        {/* Today's Birthdays - Hero Section */}
        <section className="mb-10">
          <div className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl p-8 border-4 border-pink-400 max-w-5xl mx-auto">
            <div className="flex items-center justify-center gap-4 mb-6">
              <PartyPopper className="w-12 h-12 text-pink-500 animate-bounce" />
              <h2 className="font-['Fredoka'] text-4xl md:text-5xl font-bold text-pink-600">
                Today's Birthdays!
              </h2>
              <PartyPopper className="w-12 h-12 text-pink-500 animate-bounce" style={{ animationDelay: '0.2s' }} />
            </div>
            
            {todaysBirthdays.length > 0 ? (
              <div className="flex flex-wrap justify-center gap-6">
                {todaysBirthdays.map((person, index) => (
                  <div
                    key={index}
                    className="bg-gradient-to-br from-pink-100 to-purple-100 rounded-2xl p-6 shadow-lg border-2 border-pink-300 transform hover:scale-105 transition-transform animate-pulse-slow"
                    style={{ animationDelay: `${index * 0.2}s` }}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <Cake className="w-10 h-10 text-pink-500" />
                      <div>
                        <p className="font-['Fredoka'] text-2xl md:text-3xl font-bold text-gray-800">
                          {person.name}
                        </p>
                        <p className="font-['Outfit'] text-lg text-purple-600 font-medium">
                          {person.person_type === 'staff' ? '⭐ Staff' : '🏕️ Camper'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <Gift className="w-5 h-5 text-orange-500" />
                      <span className="font-['Outfit'] text-sm text-gray-600 bg-white/60 px-3 py-1 rounded-full">
                        {person.person_type === 'staff' ? 'Staff Member' : 'Camper'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Star className="w-16 h-16 text-yellow-400 mx-auto mb-4 animate-spin-slow" />
                <p className="font-['Outfit'] text-2xl text-gray-500">
                  No birthdays today - but every day at camp is special! 🌟
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Weekly View */}
        <section>
          <h2 className="font-['Fredoka'] text-3xl md:text-4xl font-bold text-center text-white drop-shadow-lg mb-6">
            🎂 This Week's Celebrations 🎂
          </h2>
          <p className="font-['Outfit'] text-xl text-center text-white/80 mb-6">
            Week of {weeklyData?.week_start && format(parseISO(weeklyData.week_start), "MMMM d")} - {weeklyData?.week_end && format(parseISO(weeklyData.week_end), "MMMM d")}
          </p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 max-w-7xl mx-auto">
            {weeklyData?.days?.map((day, index) => (
              <div
                key={day.date}
                className={`
                  rounded-2xl p-4 shadow-lg transition-all duration-300
                  ${day.is_today 
                    ? 'bg-gradient-to-br from-pink-500 to-purple-500 text-white scale-105 border-4 border-yellow-300' 
                    : 'bg-white/80 backdrop-blur-sm'
                  }
                `}
              >
                <div className="text-center mb-3">
                  <p className={`font-['Fredoka'] text-lg font-bold ${day.is_today ? 'text-yellow-200' : 'text-gray-700'}`}>
                    {day.day_name}
                  </p>
                  <p className={`font-['Outfit'] text-sm ${day.is_today ? 'text-white/80' : 'text-gray-500'}`}>
                    {format(parseISO(day.date), "MMM d")}
                  </p>
                  {day.is_today && (
                    <span className="inline-block mt-1 px-2 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full animate-pulse">
                      TODAY
                    </span>
                  )}
                </div>
                
                <div className="space-y-2 min-h-[80px]">
                  {day.birthdays?.length > 0 ? (
                    day.birthdays.map((person, pIndex) => (
                      <div
                        key={pIndex}
                        className={`
                          text-center p-2 rounded-lg text-sm
                          ${day.is_today 
                            ? 'bg-white/20' 
                            : person.person_type === 'staff' 
                              ? 'bg-purple-100' 
                              : 'bg-pink-100'
                          }
                        `}
                      >
                        <p className={`font-['Outfit'] font-semibold truncate ${day.is_today ? 'text-white' : 'text-gray-800'}`}>
                          {person.name}
                        </p>
                        <p className={`font-['Outfit'] text-xs truncate ${day.is_today ? 'text-white/70' : 'text-gray-500'}`}>
                          {person.person_type === 'staff' ? 'Staff' : 'Camper'}
                        </p>
                      </div>
                    ))
                  ) : (
                    <p className={`text-center text-sm italic ${day.is_today ? 'text-white/60' : 'text-gray-400'}`}>
                      No birthdays
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Footer */}
        <footer className="mt-10 text-center">
          <p className="font-['Outfit'] text-white text-sm bg-blue-900 inline-block px-4 py-2 rounded-full">
            Made by Brian Stein • Auto-refreshes every 5 minutes
          </p>
        </footer>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes confetti-fall {
          0% {
            transform: translateY(-100vh) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        
        @keyframes balloon-float {
          0%, 100% {
            transform: translateY(0px) rotate(-2deg);
          }
          50% {
            transform: translateY(-20px) rotate(2deg);
          }
        }
        
        @keyframes pulse-slow {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.9;
            transform: scale(1.02);
          }
        }
        
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        
        .animate-confetti-fall {
          animation: confetti-fall linear infinite;
        }
        
        .animate-balloon-float {
          animation: balloon-float 3s ease-in-out infinite;
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 2s ease-in-out infinite;
        }
        
        .animate-spin-slow {
          animation: spin-slow 8s linear infinite;
        }
      `}</style>
    </div>
  );
}
